import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { LeadStage, Channel } from "@school-sales-ai/shared";

type FilterKey = "channel" | "scoreMin" | "scoreMax" | "search" | "from" | "to";

/** How the Pipeline de Prospectos page renders the same filtered lead set. */
export type ViewMode = "board" | "table";

type CrmState = {
  page: number;
  pageSize: number;
  channel: Channel | "";
  scoreMin: string;
  scoreMax: string;
  search: string;
  from: string;
  to: string;
  // Multi-select filters: OR inside each list, AND across them. `productIds` and
  // `cycleIds` may hold "none" ("no program" / "no cycle") next to real ids.
  // Not single-string FilterKeys, so each has its own toggle/clear actions.
  stages: LeadStage[];
  productIds: string[];
  cycleIds: string[];
  labelIds: string[];
  // «Asignado a»: team member ids, plus "none" for unassigned leads.
  assigneeIds: string[];
  setFilter: (key: FilterKey, value: string) => void;
  toggleStage: (key: LeadStage) => void;
  /** Narrow to exactly these columns — «Ver todos» on a board column. */
  setStages: (keys: LeadStage[]) => void;
  clearStages: () => void;
  toggleProduct: (id: string) => void;
  clearProducts: () => void;
  toggleCycle: (id: string) => void;
  clearCycles: () => void;
  toggleLabelFilter: (id: string) => void;
  clearLabelFilters: () => void;
  toggleAssignee: (id: string) => void;
  clearAssignees: () => void;
  setPage: (page: number) => void;
  reset: () => void;

  // Board ⇄ table toggle. The only persisted field — filters stay in memory so a
  // new session starts unfiltered, but the view someone works in is theirs.
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;

  // How many cards each column has loaded, for the ones the user scrolled past
  // the initial page. Absent = still showing the first page.
  stageLimits: Record<string, number>;
  growStage: (stageKey: string, by: number) => void;

  // Collapsed cycle swimlanes, by lane key. "Sin ciclo" starts collapsed — on the
  // pilot's biggest product it holds 90 of 101 leads, and it is the lane nobody
  // is working when they open a cycle view.
  collapsedLanes: Record<string, boolean>;
  toggleLane: (laneKey: string) => void;

  // Deleted-leads toggle (table view only — the board has no deleted-card path)
  showDeletedLeads: boolean;
  toggleShowDeletedLeads: () => void;

  // Modal open/close state
  isCreateModalOpen: boolean;
  isEditModalOpen: boolean;
  editingLeadId: string | null;
  openCreateModal: () => void;
  closeCreateModal: () => void;
  openEditModal: (leadId: string) => void;
  closeEditModal: () => void;

  // Manage-labels modal (single instance mounted in LeadsPipeline; opened from
  // the header button and from the card label popover).
  isManageLabelsOpen: boolean;
  openManageLabels: () => void;
  closeManageLabels: () => void;
};

// Filters only — what "Limpiar filtros" clears. Deliberately excludes the view
// mode, the deleted-leads toggle and modal state: clearing a search should not
// close a modal or flip the view out from under the user.
function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

const FILTER_DEFAULTS = {
  page: 1,
  channel: "" as Channel | "",
  scoreMin: "",
  scoreMax: "",
  search: "",
  from: "",
  to: "",
  stages: [] as LeadStage[],
  productIds: [] as string[],
  cycleIds: [] as string[],
  labelIds: [] as string[],
  assigneeIds: [] as string[],
  // Cleared with the filters: a limit grown against one result set means nothing
  // against the next.
  stageLimits: {} as Record<string, number>,
};

export const useCrmStore = create<CrmState>()(
  persist(
    (set) => ({
      ...FILTER_DEFAULTS,
      pageSize: 20,
      setFilter: (key, value) => set((s) => ({ ...s, [key]: value, page: 1 })),
      toggleStage: (key) => set((s) => ({ stages: toggle(s.stages, key), page: 1 })),
      setStages: (keys) => set({ stages: keys, page: 1 }),
      clearStages: () => set({ stages: [], page: 1 }),
      toggleProduct: (id) => set((s) => ({ productIds: toggle(s.productIds, id), page: 1 })),
      clearProducts: () => set({ productIds: [], page: 1 }),
      toggleCycle: (id) => set((s) => ({ cycleIds: toggle(s.cycleIds, id), page: 1 })),
      clearCycles: () => set({ cycleIds: [], page: 1 }),
      toggleLabelFilter: (id) => set((s) => ({ labelIds: toggle(s.labelIds, id), page: 1 })),
      clearLabelFilters: () => set({ labelIds: [], page: 1 }),
      toggleAssignee: (id) => set((s) => ({ assigneeIds: toggle(s.assigneeIds, id), page: 1 })),
      clearAssignees: () => set({ assigneeIds: [], page: 1 }),
      setPage: (page) => set({ page }),
      reset: () => set(FILTER_DEFAULTS),

      viewMode: "board",
      setViewMode: (mode) => set({ viewMode: mode }),

      growStage: (stageKey, by) =>
        set((s) => ({
          ...s,
          stageLimits: {
            ...s.stageLimits,
            [stageKey]: (s.stageLimits[stageKey] ?? by) + by,
          },
        })),

      collapsedLanes: { none: true },
      toggleLane: (laneKey) =>
        set((s) => ({
          ...s,
          collapsedLanes: {
            ...s.collapsedLanes,
            [laneKey]: !s.collapsedLanes[laneKey],
          },
        })),

      showDeletedLeads: false,
      toggleShowDeletedLeads: () =>
        set((s) => ({ ...s, showDeletedLeads: !s.showDeletedLeads })),

      isCreateModalOpen: false,
      isEditModalOpen: false,
      editingLeadId: null,
      openCreateModal: () => set({ isCreateModalOpen: true }),
      closeCreateModal: () => set({ isCreateModalOpen: false }),
      openEditModal: (leadId: string) =>
        set({ isEditModalOpen: true, editingLeadId: leadId }),
      closeEditModal: () => set({ isEditModalOpen: false, editingLeadId: null }),

      isManageLabelsOpen: false,
      openManageLabels: () => set({ isManageLabelsOpen: true }),
      closeManageLabels: () => set({ isManageLabelsOpen: false }),
    }),
    {
      name: "crm-view-preferences",
      partialize: (s) => ({ viewMode: s.viewMode }),
    },
  ),
);

/** True when any lead filter is narrowing the result set. */
export function hasActiveFilters(s: CrmState): boolean {
  return (
    !!s.channel ||
    !!s.scoreMin ||
    !!s.scoreMax ||
    !!s.search ||
    !!s.from ||
    !!s.to ||
    s.stages.length > 0 ||
    s.productIds.length > 0 ||
    s.cycleIds.length > 0 ||
    s.labelIds.length > 0 ||
    s.assigneeIds.length > 0
  );
}
